// VIN rules for North American vehicles (17 characters, ISO 3779 + the 49 CFR 565 check digit).

const TRANSLITERATION: Record<string, number> = {
  A: 1, B: 2, C: 3, D: 4, E: 5, F: 6, G: 7, H: 8,
  J: 1, K: 2, L: 3, M: 4, N: 5, P: 7, R: 9,
  S: 2, T: 3, U: 4, V: 5, W: 6, X: 7, Y: 8, Z: 9,
};
const WEIGHTS = [8, 7, 6, 5, 4, 3, 2, 10, 0, 9, 8, 7, 6, 5, 4, 3, 2];
const VIN_PATTERN = /^[A-HJ-NPR-Z0-9]{17}$/;

/** Upper case, no spaces or dashes: " 1hg cm8-2633a004352 " -> "1HGCM82633A004352". */
export function normalizeVin(raw: string): string {
  return raw.trim().toUpperCase().replace(/[\s-]/g, "");
}

export function vinCheckDigit(vin: string): string {
  let sum = 0;
  for (let i = 0; i < 17; i++) {
    const ch = vin[i]!;
    const value = /\d/.test(ch) ? Number(ch) : TRANSLITERATION[ch];
    if (value === undefined) return "";
    sum += value * WEIGHTS[i]!;
  }
  const rem = sum % 11;
  return rem === 10 ? "X" : String(rem);
}

export type VinProblem = "length" | "characters" | "check_digit";

/**
 * The message a rep sees for each problem. Cars from before 1981 have shorter VINs, and cars built
 * for outside North America usually fail the check digit: neither can go through the portal.
 */
function vinMessage(problem: VinProblem, vin: string): string {
  if (problem === "length") {
    const n = vin.length;
    if (n > 17) return `A VIN has 17 characters. This one has ${n}. Check for an extra character.`;
    if (n >= 11 && n <= 16) {
      return `A VIN has 17 characters. This one has ${n}. Check for a missed character. Cars from before 1981 have shorter VINs: call the office for those.`;
    }
    return `A VIN has 17 characters. This one has ${n}.`;
  }
  if (problem === "characters") {
    return /[IOQ]/.test(vin)
      ? "VINs never use the letters I, O or Q. Use 1 for I and 0 for O or Q."
      : "A VIN has only letters and digits.";
  }
  return "This VIN fails its check digit. Check each character against the plate on the dash. Cars built for outside the US and Canada can fail this check: call the office for those.";
}

export function validateVin(raw: string): { ok: true; vin: string } | { ok: false; problem: VinProblem; message: string } {
  const vin = normalizeVin(raw);
  const fail = (problem: VinProblem) => ({ ok: false as const, problem, message: vinMessage(problem, vin) });
  if (vin.length !== 17) return fail("length");
  if (!VIN_PATTERN.test(vin)) return fail("characters");
  if (vinCheckDigit(vin) !== vin[8]) return fail("check_digit");
  return { ok: true, vin };
}
